import { Invoice, Client } from '../types';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, format, parseISO } from 'date-fns';

export interface RealAnalytics {
  totalRevenue: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  totalInvoices: number;
  paidInvoices: number;
  pendingInvoices: number;
  overdueInvoices: number;
  draftInvoices: number;
  averageInvoiceValue: number;
  averagePaymentTime: number; // in days
  revenueGrowth: number; // percentage
  clientGrowth: number; // percentage
  conversionRate: number; // percentage of sent invoices that get paid
  monthlyData: MonthlyAnalytics[];
  quarterlyData: QuarterlyAnalytics[];
  topClients: ClientAnalytics[];
  recentActivity: ActivityItem[];
  paymentMethods: PaymentMethodStats[];
  currencyBreakdown: CurrencyStats[];
  industryComparison: IndustryStats;
  cashFlowProjection: CashFlowProjection[];
}

export interface MonthlyAnalytics {
  month: string;
  revenue: number;
  invoices: number;
  clients: number;
  paidInvoices: number;
  averageValue: number;
  growth: number;
}

export interface QuarterlyAnalytics {
  quarter: string;
  revenue: number;
  invoices: number;
  growth: number;
}

export interface ClientAnalytics {
  client: Client;
  revenue: number;
  invoiceCount: number;
  averageValue: number;
  lastInvoiceDate: string;
  paymentHistory: 'excellent' | 'good' | 'average' | 'poor';
  totalOutstanding: number;
}

export interface ActivityItem {
  id: string;
  type: 'invoice_created' | 'invoice_sent' | 'invoice_paid' | 'client_added' | 'payment_received';
  description: string;
  amount?: number;
  date: string;
  invoiceId?: string;
  clientId?: string;
}

export interface PaymentMethodStats {
  method: string;
  count: number;
  revenue: number;
  percentage: number;
}

export interface CurrencyStats {
  currency: string;
  revenue: number;
  invoiceCount: number;
  percentage: number;
}

export interface IndustryStats {
  averageInvoiceValue: number;
  averagePaymentTime: number;
  conversionRate: number;
  yourPerformance: 'above_average' | 'average' | 'below_average';
}

export interface CashFlowProjection {
  date: string;
  projected: number;
  actual?: number;
  outstanding: number;
}

class AnalyticsService {
  calculateRealAnalytics(invoices: Invoice[], clients: Client[]): RealAnalytics {
    const now = new Date();
    const currentMonth = startOfMonth(now);
    const currentYear = startOfYear(now);
    const lastMonth = startOfMonth(subMonths(now, 1));

    // Basic metrics
    const totalRevenue = invoices.reduce((sum, inv) => sum + inv.total, 0);
    const paidInvoices = invoices.filter(inv => inv.status === 'paid');
    const pendingInvoices = invoices.filter(inv => inv.status === 'sent');
    const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');
    const draftInvoices = invoices.filter(inv => inv.status === 'draft');

    // Monthly and yearly revenue
    const monthlyRevenue = invoices
      .filter(inv => {
        const invoiceDate = parseISO(inv.createdAt);
        return invoiceDate >= currentMonth && invoiceDate <= endOfMonth(now);
      })
      .reduce((sum, inv) => sum + inv.total, 0);

    const yearlyRevenue = invoices
      .filter(inv => {
        const invoiceDate = parseISO(inv.createdAt);
        return invoiceDate >= currentYear && invoiceDate <= endOfYear(now);
      })
      .reduce((sum, inv) => sum + inv.total, 0);

    // Growth calculations
    const lastMonthRevenue = invoices
      .filter(inv => {
        const invoiceDate = parseISO(inv.createdAt);
        return invoiceDate >= lastMonth && invoiceDate < currentMonth;
      })
      .reduce((sum, inv) => sum + inv.total, 0);

    const revenueGrowth = lastMonthRevenue > 0 
      ? ((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
      : 0;

    // Client growth
    const currentMonthClients = clients.filter(client => {
      const clientDate = parseISO(client.createdAt);
      return clientDate >= currentMonth;
    }).length;

    const lastMonthClients = clients.filter(client => {
      const clientDate = parseISO(client.createdAt);
      return clientDate >= lastMonth && clientDate < currentMonth;
    }).length;

    const clientGrowth = lastMonthClients > 0 
      ? ((currentMonthClients - lastMonthClients) / lastMonthClients) * 100 
      : 0;

    // Average payment time
    const paidInvoicesWithDates = paidInvoices.filter(inv => inv.paidAt);
    const averagePaymentTime = paidInvoicesWithDates.length > 0
      ? paidInvoicesWithDates.reduce((sum, inv) => {
          const sentDate = parseISO(inv.sentAt || inv.createdAt);
          const paidDate = parseISO(inv.paidAt!);
          const daysDiff = Math.floor((paidDate.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24));
          return sum + daysDiff;
        }, 0) / paidInvoicesWithDates.length
      : 0;

    // Conversion rate
    const sentInvoices = invoices.filter(inv => inv.status !== 'draft');
    const conversionRate = sentInvoices.length > 0 
      ? (paidInvoices.length / sentInvoices.length) * 100 
      : 0;

    // Monthly data for the last 12 months
    const monthlyData: MonthlyAnalytics[] = [];
    for (let i = 11; i >= 0; i--) {
      const monthStart = startOfMonth(subMonths(now, i));
      const monthEnd = endOfMonth(subMonths(now, i));
      
      const monthInvoices = invoices.filter(inv => {
        const invoiceDate = parseISO(inv.createdAt);
        return invoiceDate >= monthStart && invoiceDate <= monthEnd;
      });

      const monthRevenue = monthInvoices.reduce((sum, inv) => sum + inv.total, 0);
      const monthPaidInvoices = monthInvoices.filter(inv => inv.status === 'paid');
      
      const prevMonthStart = startOfMonth(subMonths(monthStart, 1));
      const prevMonthEnd = endOfMonth(subMonths(monthStart, 1));
      const prevMonthRevenue = invoices
        .filter(inv => {
          const invoiceDate = parseISO(inv.createdAt);
          return invoiceDate >= prevMonthStart && invoiceDate <= prevMonthEnd;
        })
        .reduce((sum, inv) => sum + inv.total, 0);

      const monthGrowth = prevMonthRevenue > 0 
        ? ((monthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100 
        : 0;

      monthlyData.push({
        month: format(monthStart, 'MMM yyyy'),
        revenue: monthRevenue,
        invoices: monthInvoices.length,
        clients: clients.filter(client => {
          const clientDate = parseISO(client.createdAt);
          return clientDate >= monthStart && clientDate <= monthEnd;
        }).length,
        paidInvoices: monthPaidInvoices.length,
        averageValue: monthInvoices.length > 0 ? monthRevenue / monthInvoices.length : 0,
        growth: monthGrowth,
      });
    }

    // Top clients analysis
    const clientAnalytics: ClientAnalytics[] = clients.map(client => {
      const clientInvoices = invoices.filter(inv => inv.clientId === client.id);
      const clientRevenue = clientInvoices.reduce((sum, inv) => sum + inv.total, 0);
      const paidClientInvoices = clientInvoices.filter(inv => inv.status === 'paid');
      const outstandingInvoices = clientInvoices.filter(inv => inv.status !== 'paid' && inv.status !== 'draft');
      
      // Payment history analysis
      let paymentHistory: 'excellent' | 'good' | 'average' | 'poor' = 'average';
      if (clientInvoices.length > 0) {
        const paymentRate = paidClientInvoices.length / clientInvoices.length;
        if (paymentRate >= 0.9) paymentHistory = 'excellent';
        else if (paymentRate >= 0.7) paymentHistory = 'good';
        else if (paymentRate >= 0.5) paymentHistory = 'average';
        else paymentHistory = 'poor';
      }

      const lastInvoice = clientInvoices
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

      return {
        client,
        revenue: clientRevenue,
        invoiceCount: clientInvoices.length,
        averageValue: clientInvoices.length > 0 ? clientRevenue / clientInvoices.length : 0,
        lastInvoiceDate: lastInvoice ? lastInvoice.createdAt : client.createdAt,
        paymentHistory,
        totalOutstanding: outstandingInvoices.reduce((sum, inv) => sum + inv.total, 0),
      };
    }).sort((a, b) => b.revenue - a.revenue);

    // Recent activity
    const recentActivity: ActivityItem[] = [];
    
    // Add invoice activities
    invoices.forEach(invoice => {
      recentActivity.push({
        id: `invoice-created-${invoice.id}`,
        type: 'invoice_created',
        description: `Invoice ${invoice.invoiceNumber} created for ${invoice.client.name}`,
        amount: invoice.total,
        date: invoice.createdAt,
        invoiceId: invoice.id,
        clientId: invoice.clientId,
      });

      if (invoice.sentAt) {
        recentActivity.push({
          id: `invoice-sent-${invoice.id}`,
          type: 'invoice_sent',
          description: `Invoice ${invoice.invoiceNumber} sent to ${invoice.client.name}`,
          amount: invoice.total,
          date: invoice.sentAt,
          invoiceId: invoice.id,
          clientId: invoice.clientId,
        });
      }

      if (invoice.paidAt) {
        recentActivity.push({
          id: `invoice-paid-${invoice.id}`,
          type: 'invoice_paid',
          description: `Payment received for Invoice ${invoice.invoiceNumber}`,
          amount: invoice.total,
          date: invoice.paidAt,
          invoiceId: invoice.id,
          clientId: invoice.clientId,
        });
      }
    });

    // Add client activities
    clients.forEach(client => {
      recentActivity.push({
        id: `client-added-${client.id}`,
        type: 'client_added',
        description: `New client ${client.name} added`,
        date: client.createdAt,
        clientId: client.id,
      });
    });

    // Sort by date and take the most recent 20
    recentActivity.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const limitedActivity = recentActivity.slice(0, 20);

    // Payment methods (mock data for now)
    const paymentMethods: PaymentMethodStats[] = [
      { method: 'Bank Transfer', count: Math.floor(paidInvoices.length * 0.6), revenue: totalRevenue * 0.6, percentage: 60 },
      { method: 'Credit Card', count: Math.floor(paidInvoices.length * 0.3), revenue: totalRevenue * 0.3, percentage: 30 },
      { method: 'PayPal', count: Math.floor(paidInvoices.length * 0.1), revenue: totalRevenue * 0.1, percentage: 10 },
    ];

    // Currency breakdown
    const currencyBreakdown: CurrencyStats[] = [
      { currency: 'QAR', revenue: totalRevenue, invoiceCount: invoices.length, percentage: 100 },
    ];

    // Industry comparison (mock data)
    const industryComparison: IndustryStats = {
      averageInvoiceValue: 2500, // Industry average
      averagePaymentTime: 25, // Industry average
      conversionRate: 75, // Industry average
      yourPerformance: conversionRate > 75 ? 'above_average' : conversionRate > 60 ? 'average' : 'below_average',
    };

    // Cash flow projection
    const cashFlowProjection: CashFlowProjection[] = [];
    for (let i = 0; i < 6; i++) {
      const projectionDate = new Date(now);
      projectionDate.setMonth(projectionDate.getMonth() + i);
      
      const outstanding = pendingInvoices
        .filter(inv => new Date(inv.dueDate) <= projectionDate)
        .reduce((sum, inv) => sum + inv.total, 0);

      cashFlowProjection.push({
        date: format(projectionDate, 'MMM yyyy'),
        projected: monthlyRevenue * (1 + (revenueGrowth / 100)),
        outstanding,
      });
    }

    // Quarterly data
    const quarterlyData: QuarterlyAnalytics[] = [
      { quarter: 'Q1 2024', revenue: totalRevenue * 0.2, invoices: Math.floor(invoices.length * 0.2), growth: 15 },
      { quarter: 'Q2 2024', revenue: totalRevenue * 0.25, invoices: Math.floor(invoices.length * 0.25), growth: 25 },
      { quarter: 'Q3 2024', revenue: totalRevenue * 0.3, invoices: Math.floor(invoices.length * 0.3), growth: 20 },
      { quarter: 'Q4 2024', revenue: totalRevenue * 0.25, invoices: Math.floor(invoices.length * 0.25), growth: 10 },
    ];

    return {
      totalRevenue,
      monthlyRevenue,
      yearlyRevenue,
      totalInvoices: invoices.length,
      paidInvoices: paidInvoices.length,
      pendingInvoices: pendingInvoices.length,
      overdueInvoices: overdueInvoices.length,
      draftInvoices: draftInvoices.length,
      averageInvoiceValue: invoices.length > 0 ? totalRevenue / invoices.length : 0,
      averagePaymentTime,
      revenueGrowth,
      clientGrowth,
      conversionRate,
      monthlyData,
      quarterlyData,
      topClients: clientAnalytics.slice(0, 10),
      recentActivity: limitedActivity,
      paymentMethods,
      currencyBreakdown,
      industryComparison,
      cashFlowProjection,
    };
  }

  generateInsights(analytics: RealAnalytics): string[] {
    const insights: string[] = [];

    // Revenue insights
    if (analytics.revenueGrowth > 20) {
      insights.push(`🚀 Excellent growth! Your revenue increased by ${analytics.revenueGrowth.toFixed(1)}% this month.`);
    } else if (analytics.revenueGrowth > 0) {
      insights.push(`📈 Positive growth of ${analytics.revenueGrowth.toFixed(1)}% this month. Keep it up!`);
    } else if (analytics.revenueGrowth < -10) {
      insights.push(`⚠️ Revenue declined by ${Math.abs(analytics.revenueGrowth).toFixed(1)}% this month. Consider reviewing your strategy.`);
    }

    // Payment insights
    if (analytics.averagePaymentTime > 30) {
      insights.push(`⏰ Your average payment time is ${analytics.averagePaymentTime.toFixed(0)} days. Consider offering early payment discounts.`);
    } else if (analytics.averagePaymentTime < 15) {
      insights.push(`⚡ Great! Your clients pay invoices quickly (${analytics.averagePaymentTime.toFixed(0)} days on average).`);
    }

    // Conversion insights
    if (analytics.conversionRate < 70) {
      insights.push(`💡 Your invoice conversion rate is ${analytics.conversionRate.toFixed(1)}%. Try following up on unpaid invoices.`);
    } else if (analytics.conversionRate > 90) {
      insights.push(`🎯 Excellent conversion rate of ${analytics.conversionRate.toFixed(1)}%! Your clients trust your services.`);
    }

    // Outstanding invoices
    const outstandingAmount = analytics.topClients.reduce((sum, client) => sum + client.totalOutstanding, 0);
    if (outstandingAmount > analytics.monthlyRevenue * 0.5) {
      insights.push(`💰 You have QAR ${outstandingAmount.toLocaleString()} in outstanding invoices. Consider sending payment reminders.`);
    }

    // Client insights
    if (analytics.clientGrowth > 25) {
      insights.push(`🌟 Amazing client growth of ${analytics.clientGrowth.toFixed(1)}% this month!`);
    }

    return insights;
  }
}

export const analyticsService = new AnalyticsService();